import { html, css, LitElement } from "lit";
import type { PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { state } from "lit/decorators.js";
import { query } from "lit/decorators.js";
import { hermesClient } from "../hermes-singleton.js";
import { runGatewayPoll } from "../gateway-poll-queue.ts";
import { canPollGateway } from "../poll-coordinator.ts";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import { formatMs, toJavaScriptMs } from "../format.ts";
import type { ChatMessage, ChatPanelConfig } from "../../types/ead-types.js";
import { isEadAssistantInjectMessage, isPhase1LoginInjectMessage, isPhasePromotedRunningMessage, isRunningBootstrapInjectMessage, isRunningCadenceReminderMessage, shouldSyncExecutionPhaseFromChat } from "../ead-assistant-inject.ts";
import { executionsStore } from "../executions-store.ts";
import { ProjectRunStatus, readProjectRunStatus } from "../run-status.ts";
import {
  EAD_RUN_CHAT_UPDATED,
  type RunChatUpdatedDetail,
} from "../run-chat-events.ts";

const MAX_PINNED_CADENCE_REMINDERS = 5;

const DEFAULT_CHAT_CONFIG: ChatPanelConfig = {
  sessionKey: "",
  title: "Chat",
  readonly: false,
  showTimestamps: true,
  showAvatars: true,
};
const SCROLL_IDLE_MS = 5000;

function chatConfigSemanticEqual(a: ChatPanelConfig, b: ChatPanelConfig | undefined): boolean {
  if (!b) return false;
  return (
    (a.sessionKey ?? "") === (b.sessionKey ?? "") &&
    (a.sessionKeySource ?? "") === (b.sessionKeySource ?? "") &&
    a.readonly === b.readonly &&
    Boolean(a.loginAwaitingCredentials) === Boolean(b.loginAwaitingCredentials) &&
    (a.loginGateHint ?? "") === (b.loginGateHint ?? "") &&
    Boolean(a.composerEnabled ?? true) === Boolean(b.composerEnabled ?? true) &&
    (a.title ?? "") === (b.title ?? "") &&
    (a.pollIntervalMs ?? 3000) === (b.pollIntervalMs ?? 3000) &&
    (a.statusPollIntervalMs ?? 3000) === (b.statusPollIntervalMs ?? 3000) &&
    Boolean(a.enableStatusPolling ?? true) === Boolean(b.enableStatusPolling ?? true) &&
    (a.runBriefingMarkdown ?? "") === (b.runBriefingMarkdown ?? "")
  );
}

@customElement("ead-chat-panel")
export class EadChatPanel extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      width: 100%;
      min-height: 0;
      overflow: hidden;
    }

    .chat-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      width: 100%;
      background: var(--bg-elevated, var(--panel, var(--bg, #161920)));
      border-radius: var(--radius-md, 10px);
      border: 1px solid color-mix(in srgb, var(--border, #1e2028) 88%, transparent);
      overflow: hidden;
    }

    .session-banner {
      padding: 8px 12px;
      font-size: 12px;
      line-height: 1.45;
      color: var(--text, #d4d4d8);
      background: color-mix(in srgb, var(--warn, #f59e0b) 12%, transparent);
      border-bottom: 1px solid color-mix(in srgb, var(--border) 74%, transparent);
    }

    .session-banner[data-tone="info"] {
      background: color-mix(in srgb, var(--info, #3b82f6) 12%, transparent);
    }

    .chat-header {
      padding: 12px 16px;
      border-bottom: 1px solid color-mix(in srgb, var(--border, #2e2f33) 74%, transparent);
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: color-mix(in srgb, var(--bg-elevated, var(--panel)) 96%, transparent);
      flex-shrink: 0;
    }

    .chat-header h3 {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: var(--text-strong, #f4f4f5);
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .chat-actions {
      display: flex;
      gap: 8px;
    }

    .btn-icon {
      width: 28px;
      height: 28px;
      border: none;
      background: transparent;
      color: var(--muted, #838387);
      border-radius: var(--radius-sm, 6px);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background var(--duration-fast, 100ms) ease;
    }

    .btn-icon:hover {
      background: var(--bg-hover, rgba(255, 255, 255, 0.06));
      color: var(--text-strong, #f4f4f5);
    }

    .messages {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 12px 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: 0;
    }

    .message {
      display: flex;
      gap: 10px;
      max-width: 100%;
      animation: slideIn 0.2s ease-out;
    }

    .message--user,
    .message--ead-assistant {
      justify-content: flex-end;
    }

    .message--user .message-body,
    .message--ead-assistant .message-body {
      max-width: min(78%, 720px);
    }

    .message--ead-assistant .message-meta {
      justify-content: flex-end;
    }

    .message--assistant .message-body,
    .message--system .message-body,
    .message--tool .message-body {
      max-width: min(86%, 760px);
    }

    .message--user .message-content,
    .message--ead-assistant .message-content {
      background: color-mix(in srgb, var(--accent, #ff5c5c) 12%, transparent);
      border: 1px solid color-mix(in srgb, var(--accent, #ff5c5c) 28%, transparent);
      padding: 8px 10px;
      border-radius: 10px;
    }

    .message--assistant .message-content,
    .message--system .message-content,
    .message--tool .message-content {
      background: color-mix(in srgb, var(--bg-elevated, #161920) 88%, transparent);
      border: 1px solid color-mix(in srgb, var(--border, #2e2f33) 72%, transparent);
      padding: 8px 10px;
      border-radius: 10px;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .message-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 600;
      flex-shrink: 0;
    }

    .message-avatar.user,
    .message-avatar.ead-assistant {
      background: color-mix(in srgb, var(--accent, #ff5c5c) 16%, transparent);
      color: var(--accent);
    }

    .message-avatar.ead-assistant {
      font-size: 9px;
      letter-spacing: -0.04em;
    }

    .message-avatar.assistant {
      background: color-mix(in srgb, var(--ok, #22c55e) 18%, transparent);
      color: var(--ok);
    }

    .message-avatar.system {
      background: color-mix(in srgb, var(--warn, #f59e0b) 14%, transparent);
      color: var(--warn);
    }

    .message-avatar.tool {
      background: color-mix(in srgb, var(--info, #3b82f6) 14%, transparent);
      color: var(--info, #3b82f6);
    }

    .message-body {
      flex: 1;
      min-width: 0;
    }

    .message-meta {
      font-size: 10px;
      color: var(--muted, #838387);
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .message-context {
      font-size: 10px;
      padding: 2px 6px;
      border-radius: 4px;
      background: color-mix(in srgb, var(--accent) 10%, transparent);
      color: var(--accent);
    }

    .message-content {
      font-size: 13px;
      line-height: 1.55;
      color: var(--text, #d4d4d8);
      word-wrap: break-word;
    }

    .message-content p {
      margin: 0 0 8px;
    }

    .message-content p:last-child {
      margin-bottom: 0;
    }

    .message-content h1,
    .message-content h2,
    .message-content h3,
    .message-content h4 {
      margin: 12px 0 8px;
      line-height: 1.35;
      color: var(--text-strong, #f4f4f5);
      letter-spacing: 0.01em;
    }

    .message-content h2 {
      font-size: 14px;
      border-bottom: 1px solid color-mix(in srgb, var(--border, #2e2f33) 66%, transparent);
      padding-bottom: 4px;
    }

    .message-content h3 {
      font-size: 13px;
    }

    .message-content h4 {
      font-size: 12px;
      text-transform: uppercase;
      color: color-mix(in srgb, var(--text, #d4d4d8) 88%, transparent);
    }

    .message-content ul,
    .message-content ol {
      margin: 8px 0 10px;
      padding-left: 22px;
      list-style-position: outside;
    }

    .message-content ol > li {
      margin: 10px 0;
    }

    .message-content ol > li > ul {
      margin: 6px 0 2px;
      padding-left: 18px;
    }

    .message-content li {
      margin: 4px 0;
    }

    .message-content strong {
      color: var(--text-strong, #f4f4f5);
    }

    .tool-compact {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      min-width: 0;
      font-size: 12px;
      line-height: 1.4;
      color: var(--text, #d4d4d8);
    }

    .tool-compact__line {
      min-width: 0;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-family: var(--mono, ui-monospace, SFMono-Regular, Menlo, monospace);
      font-size: 11px;
      color: color-mix(in srgb, var(--text, #d4d4d8) 85%, transparent);
    }

    .tool-compact__icon {
      width: 18px;
      height: 18px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 999px;
      border: 1px solid color-mix(in srgb, var(--info, #3b82f6) 34%, transparent);
      background: color-mix(in srgb, var(--info, #3b82f6) 12%, transparent);
      color: var(--info, #3b82f6);
      font-size: 11px;
      font-weight: 700;
      cursor: help;
      flex-shrink: 0;
    }

    .message-content pre {
      background: color-mix(in srgb, var(--bg, #0e1015) 88%, transparent);
      padding: 8px 12px;
      border-radius: var(--radius-sm, 6px);
      overflow-x: auto;
      font-size: 11px;
      margin: 8px 0;
    }

    .message-content code {
      background: color-mix(in srgb, var(--bg-muted, #1f2330) 90%, transparent);
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 12px;
    }

    .message-artifacts {
      display: flex;
      gap: 8px;
      margin-top: 8px;
      flex-wrap: wrap;
    }

    .artifact {
      padding: 6px 10px;
      border-radius: var(--radius-sm, 6px);
      border: 1px solid color-mix(in srgb, var(--border) 80%, transparent);
      background: color-mix(in srgb, var(--bg-elevated) 72%, transparent);
      font-size: 11px;
      color: var(--accent);
      cursor: pointer;
      transition: border-color var(--duration-fast, 100ms) ease;
    }

    .artifact:hover {
      background: color-mix(in srgb, var(--accent) 12%, transparent);
      border-color: color-mix(in srgb, var(--accent) 28%, transparent);
    }

    .input-area {
      padding: 12px 16px;
      border-top: 1px solid color-mix(in srgb, var(--border) 74%, transparent);
      background: color-mix(in srgb, var(--bg-elevated) 92%, transparent);
      flex-shrink: 0;
    }

    .input-wrapper {
      display: flex;
      gap: 8px;
      align-items: flex-end;
    }

    .input-field {
      flex: 1;
      padding: 8px 12px;
      border-radius: var(--radius-md, 10px);
      border: 1px solid color-mix(in srgb, var(--border, #2e2f33) 88%, transparent);
      background: var(--bg, #0e1015);
      color: var(--text-strong, #f4f4f5);
      font-size: 13px;
      font-family: inherit;
      resize: none;
      min-height: 36px;
      max-height: 140px;
      line-height: 1.45;
      white-space: pre-wrap;
      overflow-y: auto;
    }

    .input-field:focus {
      outline: none;
      border-color: color-mix(in srgb, var(--accent, #ff5c5c) 45%, var(--border));
      box-shadow: var(--focus-ring, 0 0 0 2px transparent);
    }

    .input-field:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .btn-send {
      width: 36px;
      height: 36px;
      border: none;
      background: var(--accent);
      color: var(--accent-foreground, #fafafa);
      border-radius: var(--radius-md, 10px);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity var(--duration-fast, 100ms) ease;
      flex-shrink: 0;
    }

    .btn-send:hover:not(:disabled) {
      filter: brightness(1.08);
    }

    .btn-send:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .typing-indicator {
      padding: 8px 4px;
      font-size: 12px;
      color: var(--muted);
      font-style: italic;
      display: flex;
      align-items: center;
      gap: 8px;
      max-width: 100%;
    }

    .typing-indicator--outbound {
      align-self: flex-end;
      margin-left: auto;
      justify-content: flex-end;
      font-style: normal;
      font-weight: 500;
      color: color-mix(in srgb, var(--accent, #ff5c5c) 88%, var(--text, #d4d4d8));
      padding: 6px 12px;
      border-radius: 10px;
      background: color-mix(in srgb, var(--accent, #ff5c5c) 10%, transparent);
      border: 1px solid color-mix(in srgb, var(--accent, #ff5c5c) 26%, transparent);
    }

    .typing-indicator--inbound {
      align-self: flex-start;
    }

    .btn-send[data-sending="true"] {
      opacity: 1;
      cursor: wait;
    }

    .btn-send__spinner {
      display: inline-block;
      width: 14px;
      height: 14px;
      border: 2px solid color-mix(in srgb, #fff 35%, transparent);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.75s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .typing-dots {
      display: flex;
      gap: 4px;
    }

    .typing-dots span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--muted);
      animation: typing 1.4s infinite ease-in-out;
    }

    .typing-dots span:nth-child(2) {
      animation-delay: 0.2s;
    }

    .typing-dots span:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes typing {
      0%,
      80%,
      100% {
        opacity: 0.3;
        transform: scale(0.8);
      }
      40% {
        opacity: 1;
        transform: scale(1);
      }
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--muted);
      font-size: 13px;
      text-align: center;
      gap: 8px;
    }

    .loading-state {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--muted);
      font-size: 13px;
    }

    .messages::-webkit-scrollbar {
      width: 6px;
    }

    .messages::-webkit-scrollbar-track {
      background: transparent;
    }

    .messages::-webkit-scrollbar-thumb {
      background: color-mix(in srgb, var(--muted) 40%, transparent);
      border-radius: var(--radius-full, 9999px);
    }
  `;

  @property({
    attribute: false,
    hasChanged(newVal: ChatPanelConfig, oldVal: ChatPanelConfig | undefined) {
      return !chatConfigSemanticEqual(newVal, oldVal);
    },
  })
  config: ChatPanelConfig = { ...DEFAULT_CHAT_CONFIG };

  @state() private _messages: ChatMessage[] = [];
  @state() private _loading = true;
  @state() private _sending = false;
  @state() private _agentActive = false;
  @state() private _inputValue = "";
  @state() private _errorMessage = "";
  /** User messages shown before the server history includes them. */
  private _pendingOutbound: ChatMessage[] = [];
  /** Messages waiting for live run_session_key / gateway readiness. */
  private _deliveryQueue: string[] = [];
  private _flushQueueInFlight = false;
  /** Timer handle for periodic history reload. */
  private _pollTimer: ReturnType<typeof setInterval> | null = null;
  private _statusPollTimer: ReturnType<typeof setInterval> | null = null;
  /** One-off reloads after send so assistant replies appear without waiting for the next poll tick. */
  private _historyBurstTimers: number[] = [];
  private _deferredScrollTimer: ReturnType<typeof setTimeout> | null = null;
  /** Last applied sessionKey after reset (normalized). */
  private _appliedSessionKey = "";
  /** Session key that has already been created/verified for this mount. */
  private _readySessionKey = "";
  private _historyLimit = 100;
  private _historyTotal = 0;
  @query(".messages") private _messagesEl?: HTMLElement;
  private _typingHoldUntil = 0;
  private _scrollHoldUntil = 0;
  private _messagesSig = "";
  /** Prevent overlapping poll requests when gateway is slow. */
  private _historyLoadInFlight = false;
  private _historyReloadQueued = false;
  private _statusPollInFlight = false;
  /** Running bootstrap inject lives outside the latest history window — fetch once and merge. */
  @state() private _pinnedRunningBootstrap: ChatMessage | null = null;
  private _runningBootstrapFetchAttempted = false;
  /** Phase 1 login gate inject is early in the transcript — pin when the tail window omits it. */
  @state() private _pinnedPhase1LoginInject: ChatMessage | null = null;
  private _phase1LoginFetchAttempted = false;
  /** Running cadence reminders can fall outside the latest history window — pin recent ones. */
  @state() private _pinnedCadenceReminders: ChatMessage[] = [];
  private _cadenceRemindersFetchAttempted = false;
  private readonly _onRunChatUpdated = (ev: Event): void => {
    const detail = (ev as CustomEvent<RunChatUpdatedDetail>).detail;
    const sessionKey = (this.config.sessionKey ?? "").trim();
    if (!detail?.sessionKey || detail.sessionKey !== sessionKey) return;
    const userMessage = String(detail.userMessage || "").trim();
    if (userMessage) {
      this._appendOptimisticUserMessage(userMessage, { queued: false });
    }
    void this._loadHistory();
    this._scheduleHistoryReloadBurst();
    this._scrollToBottomSoon();
  };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener(EAD_RUN_CHAT_UPDATED, this._onRunChatUpdated);
  }

  disconnectedCallback(): void {
    window.removeEventListener(EAD_RUN_CHAT_UPDATED, this._onRunChatUpdated);
    this._clearPollTimer();
    this._clearStatusPollTimer();
    this._clearHistoryBurstTimers();
    this._clearDeferredScrollTimer();
    super.disconnectedCallback();
  }

  protected updated(changed: PropertyValues<this>): void {
    super.updated(changed);
    if (!changed.has("config")) {
      return;
    }
    const key = (this.config?.sessionKey ?? "").trim();
    const pollMs = Math.max(1500, this.config.pollIntervalMs ?? 3000);
    const statusPollEnabled = this.config.enableStatusPolling !== false;
    const statusPollMs = Math.max(3000, this.config.statusPollIntervalMs ?? 3000);

    if (key !== this._appliedSessionKey) {
      this._appliedSessionKey = key;
      this._readySessionKey = "";
      this._clearPollTimer();
      this._clearStatusPollTimer();
      this._clearHistoryBurstTimers();
      this._clearDeferredScrollTimer();
      this._messages = [];
      this._messagesSig = "";
      this._historyTotal = 0;
      this._pinnedRunningBootstrap = null;
      this._runningBootstrapFetchAttempted = false;
      this._pinnedPhase1LoginInject = null;
      this._phase1LoginFetchAttempted = false;
      this._pinnedCadenceReminders = [];
      this._cadenceRemindersFetchAttempted = false;
      this._pendingOutbound = [];
      this._deliveryQueue = [];
      this._sending = false;
      this._agentActive = false;
      this._inputValue = "";
      this._errorMessage = "";
      if (!key) {
        this._loading = false;
        return;
      }
      this._loading = !(this.config.runBriefingMarkdown ?? "").trim();
      void this._loadHistory();
      this._pollTimer = setInterval(() => void this._loadHistory({ fromPoll: true }), pollMs);
      if (statusPollEnabled) {
        this._statusPollTimer = setInterval(() => void this._pollAgentStatus(), statusPollMs);
      }
      return;
    }

    if (key) {
      this._clearPollTimer();
      this._pollTimer = setInterval(() => void this._loadHistory({ fromPoll: true }), pollMs);
      this._clearStatusPollTimer();
      if (statusPollEnabled) {
        this._statusPollTimer = setInterval(() => void this._pollAgentStatus(), statusPollMs);
      }
    }

    if (this._deliveryQueue.length) {
      void this._flushDeliveryQueue();
    }
  }

  /** Live runs wait for gateway session; finished runs deliver immediately when composer is on. */
  private _canDeliverChatNow(): boolean {
    if (this.config.readonly) return false;
    if ((this.config.sessionKeySource ?? "").trim() === "live") return true;
    return this.config.composerEnabled !== false;
  }

  private _isLiveSessionReady(): boolean {
    return (this.config.sessionKeySource ?? "").trim() === "live";
  }

  private _appendOptimisticUserMessage(content: string, opts?: { queued?: boolean }): void {
    const now = Date.now();
    const optimistic: ChatMessage = {
      role: "user",
      content,
      timestamp: now,
      label: opts?.queued ? "You · queued" : "You",
    };
    this._pendingOutbound = [...this._pendingOutbound, optimistic];
    this._messages = this._mergeHistoryWithPending(this._messages);
    this._errorMessage = "";
    this._inputValue = "";
    this._scrollToBottomSoon();
  }

  private async _flushDeliveryQueue(): Promise<void> {
    if (this._flushQueueInFlight || !this._deliveryQueue.length) return;
    if (!this._canDeliverChatNow()) return;
    const sessionKey = (this.config.sessionKey ?? "").trim();
    if (!sessionKey) return;

    this._flushQueueInFlight = true;
    try {
      const ready = await this._ensureSession(sessionKey);
      if (!ready) return;

      while (this._deliveryQueue.length > 0) {
        while (
          this._deliveryQueue.length > 1 &&
          this._deliveryQueue[0].trim() === this._deliveryQueue[1].trim()
        ) {
          this._deliveryQueue.shift();
        }
        const content = this._deliveryQueue[0];
        const delivered = await this._deliverMessage(sessionKey, content, { fromQueue: true });
        if (!delivered) break;
        if (this._deliveryQueue[0] === content) {
          this._deliveryQueue.shift();
        } else {
          break;
        }
      }
    } finally {
      this._flushQueueInFlight = false;
    }
  }

  private _clearPollTimer(): void {
    if (this._pollTimer !== null) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private _clearStatusPollTimer(): void {
    if (this._statusPollTimer !== null) {
      clearInterval(this._statusPollTimer);
      this._statusPollTimer = null;
    }
  }

  private async _pollAgentStatus(): Promise<void> {
    const sessionKey = (this.config.sessionKey ?? "").trim();
    if (!sessionKey || !canPollGateway()) return;
    if (this._statusPollInFlight) return;
    this._statusPollInFlight = true;
    try {
      const result = await runGatewayPoll(() =>
        hermesClient.request<{ agent_active?: boolean }>("chat.status", {
          sessionKey,
        }),
      );
      this._agentActive = result?.agent_active === true;
    } finally {
      this._statusPollInFlight = false;
    }
  }

  /** SessionDB timestamps are Unix seconds; optimistic sends use Date.now() ms. */
  private _sameUserMessage(
    server: ChatMessage,
    pending: ChatMessage,
    pendingText: string,
  ): boolean {
    if (server.role !== "user") return false;
    if (String(server.content || "").trim() !== pendingText) return false;
    const serverMs = toJavaScriptMs(Number(server.timestamp || 0));
    const pendingMs = toJavaScriptMs(Number(pending.timestamp || 0));
    return Math.abs(serverMs - pendingMs) < 120_000;
  }

  private _mergeHistoryWithPending(serverMessages: ChatMessage[]): ChatMessage[] {
    if (!this._pendingOutbound.length) return serverMessages;
    const remaining: ChatMessage[] = [];
    for (const pending of this._pendingOutbound) {
      const text = String(pending.content || "").trim();
      const found = serverMessages.some((msg) => this._sameUserMessage(msg, pending, text));
      if (!found) remaining.push(pending);
    }
    this._pendingOutbound = remaining;
    if (!remaining.length) return serverMessages;
    return [...serverMessages, ...remaining].sort(
      (a, b) => Number(a.timestamp || 0) - Number(b.timestamp || 0),
    );
  }

  private _clearHistoryBurstTimers(): void {
    for (const id of this._historyBurstTimers) {
      window.clearTimeout(id);
    }
    this._historyBurstTimers = [];
  }

  /** After the user sends a message, history may lag one poll interval; extra loads make replies feel prompt. */
  private _scheduleHistoryReloadBurst(): void {
    this._clearHistoryBurstTimers();
    for (const ms of [400, 1000, 2200, 4000, 7000, 12000]) {
      this._historyBurstTimers.push(
        window.setTimeout(() => {
          void this._loadHistory();
        }, ms),
      );
    }
  }

  private _clearDeferredScrollTimer(): void {
    if (this._deferredScrollTimer !== null) {
      clearTimeout(this._deferredScrollTimer);
      this._deferredScrollTimer = null;
    }
  }

  private _signature(messages: ChatMessage[]): string {
    const len = messages.length;
    const last = len > 0 ? messages[len - 1] : undefined;
    let injectTail = 0;
    for (let i = Math.max(0, len - 8); i < len; i += 1) {
      const content = String(messages[i]?.content || "");
      if (isRunningCadenceReminderMessage(content)) {
        injectTail += content.length + Number(messages[i]?.timestamp ?? 0);
      }
    }
    const pinnedCadence = this._pinnedCadenceReminders
      .map((m) => `${m.timestamp ?? 0}|${String(m.content || "").length}`)
      .join(";");
    return `${len}|${last?.timestamp ?? 0}|${last?.role ?? ""}|${String(last?.content ?? "").length}|${injectTail}|${pinnedCadence}`;
  }

  private async _loadHistory(opts?: { fromPoll?: boolean }): Promise<void> {
    if (opts?.fromPoll && !canPollGateway()) {
      return;
    }
    if (this._historyLoadInFlight) {
      this._historyReloadQueued = true;
      return;
    }
    this._historyLoadInFlight = true;
    const sessionKey = (this.config.sessionKey ?? "").trim();
    try {
      await runGatewayPoll(async () => {
        await this._loadHistoryBody(sessionKey);
      });
    } finally {
      this._historyLoadInFlight = false;
      if (this._historyReloadQueued) {
        this._historyReloadQueued = false;
        void this._loadHistory(opts);
      }
    }
  }

  private async _loadHistoryBody(sessionKey: string): Promise<void> {
    try {
      if (!sessionKey) {
        return;
      }
      // Show operator briefing immediately; do not block the whole pane on network.
      if ((this.config.runBriefingMarkdown ?? "").trim()) {
        this._loading = false;
      }

      const historyOffsetForTotal = (total: number): number =>
        total > this._historyLimit ? Math.max(0, total - this._historyLimit) : 0;

      let offset = historyOffsetForTotal(this._historyTotal);
      let result = await hermesClient.request<{ messages: ChatMessage[]; total?: number; totalCount?: number }>(
        "chat.history",
        {
          sessionKey,
          limit: this._historyLimit,
          offset,
        },
      );

      // Read-only history must not depend on sessions.create — finished runs may
      // block new session creation while the transcript already exists in SessionDB.
      if (!this.config.readonly && !result?.messages?.length) {
        const ready = await this._ensureSession(sessionKey);
        if (ready) {
          result = await hermesClient.request<{ messages: ChatMessage[]; total?: number }>("chat.history", {
            sessionKey,
            limit: this._historyLimit,
            offset,
          });
        }
      }

      if (result?.messages) {
        const nextTotal = Math.max(
          0,
          Number(result.total ?? result.totalCount ?? this._historyTotal ?? 0),
        );
        this._historyTotal = nextTotal;
        const latestOffset = historyOffsetForTotal(nextTotal);
        // Always align onto the latest window when the transcript is longer than one page.
        if (latestOffset > 0 && offset !== latestOffset) {
          const latest = await hermesClient.request<{
            messages: ChatMessage[];
            total?: number;
            totalCount?: number;
          }>("chat.history", {
            sessionKey,
            limit: this._historyLimit,
            offset: latestOffset,
          });
          if (latest?.messages) {
            result.messages = latest.messages;
            this._historyTotal = Math.max(
              0,
              Number(latest.total ?? latest.totalCount ?? nextTotal),
            );
          }
        }
        const nextMessages = this._mergeHistoryWithPending(result.messages);
        await this._ensureRunningBootstrapVisible(sessionKey, nextMessages);
        await this._ensurePhase1LoginInjectVisible(sessionKey, nextMessages);
        await this._ensureCadenceRemindersVisible(sessionKey, nextMessages);
        const displayMessages = this._mergePinnedCadenceReminders(
          this._mergePinnedPhase1LoginInject(
            this._mergePinnedRunningBootstrap(nextMessages),
          ),
        );
        const nextSig = this._signature(displayMessages);
        if (nextSig !== this._messagesSig) {
          const el = this._messagesEl;
          const preserveScroll = Boolean(el) && !this._shouldAutoScroll(el);
          const prevTop = el?.scrollTop ?? 0;
          this._messages = displayMessages;
          this._messagesSig = nextSig;
          this._errorMessage = "";
          if (this.config.executionId) {
            const selected = executionsStore.selectedExecution;
            const matchesSelected =
              selected?.id === this.config.executionId
              || selected?.id === String(this.config.executionId || "").trim();
            if (matchesSelected && readProjectRunStatus(selected) === ProjectRunStatus.Initializing) {
              executionsStore.syncSelectedExecutionPhase();
            } else if (
              displayMessages.some((m) => shouldSyncExecutionPhaseFromChat(String(m.content || "")))
            ) {
              executionsStore.syncSelectedExecutionPhase();
            }
          }
          requestAnimationFrame(() => {
            const nowEl = this._messagesEl;
            if (!nowEl) return;
            if (preserveScroll) {
              nowEl.scrollTop = prevTop;
            }
            this._scrollToBottomSoon();
          });
        }
      }
      this._loading = false;
    } catch {
      const detail = hermesClient.getLastRequestError();
      if (detail) {
        this._errorMessage =
          `Chat history could not be loaded (${detail}). Click ↻ to retry. Your transcript is still stored on the gateway.`;
        this.requestUpdate();
      }
    }
  }

  private _isDuplicateOutbound(content: string): boolean {
    const trimmed = content.trim();
    if (!trimmed) return true;
    if (!this._agentActive) {
      for (let i = this._messages.length - 1; i >= 0; i -= 1) {
        const msg = this._messages[i];
        if (msg.role !== "user") continue;
        if (String(msg.content || "").trim() === trimmed) {
          return false;
        }
        break;
      }
    }
    const lastQueued = this._deliveryQueue[this._deliveryQueue.length - 1];
    if (String(lastQueued || "").trim() === trimmed) return true;
    for (let i = this._pendingOutbound.length - 1; i >= 0; i -= 1) {
      const pending = String(this._pendingOutbound[i]?.content || "").trim();
      if (!pending) continue;
      if (pending === trimmed) return true;
      break;
    }
    for (let i = this._messages.length - 1; i >= 0; i -= 1) {
      const msg = this._messages[i];
      if (msg.role !== "user") continue;
      if (String(msg.content || "").trim() === trimmed) return true;
      break;
    }
    return false;
  }

  private async _sendMessage(): Promise<void> {
    const content = this._inputValue.trim();
    const sessionKey = (this.config.sessionKey ?? "").trim();
    if (!content || !sessionKey || this.config.readonly) {
      return;
    }
    if (this._isDuplicateOutbound(content)) {
      this._inputValue = "";
      return;
    }

    const deliverNow = this._canDeliverChatNow();
    this._appendOptimisticUserMessage(content, { queued: !deliverNow });

    if (!deliverNow) {
      this._deliveryQueue = [...this._deliveryQueue, content];
      void this._flushDeliveryQueue();
      return;
    }

    const ready = await this._ensureSession(sessionKey);
    if (!ready) {
      this._deliveryQueue = [...this._deliveryQueue, content];
      this._errorMessage = "Message queued — will send when the run session is ready.";
      void this._flushDeliveryQueue();
      return;
    }

    this._sending = true;
    void this._deliverMessage(sessionKey, content);
  }

  private async _deliverMessage(
    sessionKey: string,
    content: string,
    opts?: { fromQueue?: boolean },
  ): Promise<boolean> {
    const trimmed = content.trim();
    const sendResult = await hermesClient.request<{
      sent?: boolean;
      async?: boolean;
      message?: string;
    }>("chat.send", {
      sessionKey,
      content,
      deliver: true,
    });

    this._sending = false;
    if (sendResult?.async) {
      void this._pollAgentStatus();
      this._scheduleHistoryReloadBurst();
      await this._loadHistory();
      return true;
    }
    if (!sendResult) {
      // Gateway may have persisted the user row before agent delivery failed.
      await this._loadHistory();
      const persisted = this._messages.some(
        (msg) => msg.role === "user" && String(msg.content || "").trim() === trimmed,
      );
      if (persisted) {
        this._pendingOutbound = this._pendingOutbound.filter(
          (msg) => String(msg.content || "").trim() !== trimmed,
        );
        this._errorMessage =
          hermesClient.getLastRequestError() ||
          "Message saved but the agent may still be busy. Watch the chat for a reply.";
        this._scheduleHistoryReloadBurst();
        void this._pollAgentStatus();
        return true;
      }
      if (!opts?.fromQueue) {
        this._pendingOutbound = this._pendingOutbound.filter(
          (msg) => String(msg.content || "").trim() !== trimmed,
        );
        this._messages = this._mergeHistoryWithPending(
          this._messages.filter((msg) => String(msg.content || "").trim() !== trimmed),
        );
        this._inputValue = content;
      }
      this._errorMessage =
        hermesClient.getLastRequestError() ||
        "Message could not be sent. Check connectivity and try again.";
      return false;
    }

    void this._pollAgentStatus();
    await this._loadHistory();
    this._scheduleHistoryReloadBurst();
    return true;
  }

  private _scrollToBottomSoon(): void {
    this._clearDeferredScrollTimer();
    requestAnimationFrame(() => {
      const el = this._messagesEl;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    });
  }

  private _shouldAutoScroll(el: HTMLElement): boolean {
    const now = Date.now();
    return now >= this._typingHoldUntil && now >= this._scrollHoldUntil;
  }

  private async _ensureSession(sessionKey: string): Promise<boolean> {
    if (!sessionKey) return false;
    if (this._readySessionKey === sessionKey) return true;
    const created = await hermesClient.request("sessions.create", {
      key: sessionKey,
      label: this.config.title || "Project Chat",
    });
    if (!created) {
      return false;
    }
    this._readySessionKey = sessionKey;
    return true;
  }

  private _handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void this._sendMessage();
    }
  }

  private _autoResizeComposer(e: Event): void {
    const el = e.target as HTMLTextAreaElement;
    this._typingHoldUntil = Date.now() + 1200;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }

  private _markScrollInteraction(): void {
    this._scrollHoldUntil = Date.now() + SCROLL_IDLE_MS;
    this._clearDeferredScrollTimer();
  }

  private _isInternalSystemMessage(content: string): boolean {
    const text = (content || "").trim();
    if (!text) return true;
    if (text.includes("[EAD-RUN-ACK:v1]")) return true;
    if (text.includes("[PFM-PHASE1-CANONICAL-BASELINE:v1]")) return true;
    if (text.includes("[PFM-MINDMAP-CACHE:v1]")) return true;
    if (text.includes("[PFM-POST-LOGIN-SKILLS:v1]")) return true;
    if (text.startsWith("RUN SCOPE (this session only):")) return true;
    if (text.startsWith("HARD PROJECT TEMPLATE BOUNDARY:")) return true;
    return false;
  }

  private _filterMessages(messages: ChatMessage[]): ChatMessage[] {
    const filtered = messages.filter((msg) => {
      if (msg.role === "tool") {
        return false;
      }
      if (
        msg.role === "system" &&
        this._isInternalSystemMessage(String(msg.content || ""))
      ) {
        return false;
      }
      if (
        msg.role === "assistant" &&
        this._isExecutorChatterAssistantMessage(String(msg.content || ""))
      ) {
        return false;
      }
      if (!this.config.contextFilter) {
        return true;
      }
      if (
        this.config.contextFilter?.nodeId &&
        msg.relatedNodeId !== this.config.contextFilter.nodeId
      ) {
        return false;
      }

      if (this.config.contextFilter?.timeRange) {
        const [start, end] = this.config.contextFilter.timeRange;
        if (
          msg.timestamp !== undefined &&
          (msg.timestamp < start || msg.timestamp > end)
        ) {
          return false;
        }
      }

      return true;
    });
    return filtered;
  }

  private _messageDisplayRole(msg: ChatMessage): string {
    const role = msg.role || "user";
    if (role === "user" && isEadAssistantInjectMessage(String(msg.content || ""))) {
      return "ead-assistant";
    }
    return role;
  }

  private _isInternalBootstrapAssistantMessage(content: string): boolean {
    const text = (content || "").trim();
    if (!text) return false;
    if (text.includes("## Run Introduction")) return false;
    return text.includes("[EAD-RUN-ACK:v1]");
  }

  private _isRunIntroductionMessage(content: string): boolean {
    const text = (content || "").trim();
    return text.startsWith("## Run Introduction") || text.includes("[EAD-RUN-ACK:v1]");
  }

  private _isExecutorChatterAssistantMessage(content: string): boolean {
    const text = (content || "").trim();
    if (!text) return true;
    if (/^\[Node-Report-Reply-To:/i.test(text)) return false;
    if (/^#{1,3}\s+node\s+/i.test(text)) return false;
    if (this._isRunIntroductionMessage(text)) return false;
    if (this._isInternalBootstrapAssistantMessage(text)) return true;
    const lower = text.toLowerCase();

    // Keep high-signal, user-facing summaries and explicit acknowledgements.
    if (
      lower.includes("i have received the assignment") ||
      lower.includes("i have received the run assignment") ||
      lower.includes("about this project") ||
      lower.includes("login step report") ||
      lower.includes("login succeeded") ||
      lower.includes("login phase complete") ||
      lower.includes("pfm") ||
      lower.includes("ead")
    ) {
      return false;
    }

    // Hide low-level agent narration that is already represented in the Activity log.
    return (
      lower.startsWith("i'll load ") ||
      lower.startsWith("now let me ") ||
      lower.startsWith("excellent. i have comprehensive context") ||
      lower.startsWith("login field filled") ||
      lower.startsWith("credentials entered") ||
      lower.startsWith("screenshot captured") ||
      lower.startsWith("submit button clicked") ||
      lower.startsWith("clean console") ||
      lower === "todo"
    );
  }

  private _sessionSubtitle(): string {
    const sk = (this.config.sessionKey ?? "").trim();
    if (!sk) return "—";
    return sk.length > 28 ? `${sk.slice(0, 28)}…` : sk;
  }

  private _shouldEnsureRunningBootstrapVisible(): boolean {
    const executionId = String(this.config.executionId || "").trim();
    if (!executionId) return false;
    const selected = executionsStore.selectedExecution;
    if (!selected?.id || selected.id !== executionId) return false;
    if (readProjectRunStatus(selected) !== ProjectRunStatus.Running) return false;
    return Number(selected.runningPhaseStartedAtMs || 0) > 0;
  }

  private _messageContentKey(msg: ChatMessage): string {
    return `${msg.role ?? ""}|${String(msg.content ?? "").slice(0, 160)}`;
  }

  private _shouldEnsurePhase1LoginInjectVisible(): boolean {
    const executionId = String(this.config.executionId || "").trim();
    if (!executionId) return false;
    const selected = executionsStore.selectedExecution;
    if (!selected?.id || selected.id !== executionId) return false;
    return readProjectRunStatus(selected) === ProjectRunStatus.Initializing;
  }

  private _mergePinnedPhase1LoginInject(messages: ChatMessage[]): ChatMessage[] {
    const pinned = this._pinnedPhase1LoginInject;
    if (!pinned) return messages;
    const key = this._messageContentKey(pinned);
    if (messages.some((msg) => this._messageContentKey(msg) === key)) {
      return messages;
    }
    const merged = [...messages, pinned];
    merged.sort((a, b) => Number(a.timestamp ?? 0) - Number(b.timestamp ?? 0));
    return merged;
  }

  private async _ensurePhase1LoginInjectVisible(
    sessionKey: string,
    loadedMessages: ChatMessage[],
  ): Promise<void> {
    if (!this._shouldEnsurePhase1LoginInjectVisible()) {
      this._pinnedPhase1LoginInject = null;
      this._phase1LoginFetchAttempted = false;
      return;
    }
    if (loadedMessages.some((msg) => isPhase1LoginInjectMessage(String(msg.content || "")))) {
      this._pinnedPhase1LoginInject = null;
      return;
    }
    if (this._pinnedPhase1LoginInject || this._phase1LoginFetchAttempted) {
      return;
    }
    this._phase1LoginFetchAttempted = true;
    const result = await hermesClient.request<{ messages: ChatMessage[]; total?: number }>(
      "chat.history",
      { sessionKey, limit: 80, offset: 0 },
    );
    const batch = result?.messages ?? [];
    const found = batch.find((msg) => isPhase1LoginInjectMessage(String(msg.content || "")));
    if (found) {
      this._pinnedPhase1LoginInject = found;
    }
  }

  private _mergePinnedRunningBootstrap(messages: ChatMessage[]): ChatMessage[] {
    const pinned = this._pinnedRunningBootstrap;
    if (!pinned) return messages;
    const key = this._messageContentKey(pinned);
    if (messages.some((msg) => this._messageContentKey(msg) === key)) {
      return messages;
    }
    const merged = [...messages, pinned];
    merged.sort((a, b) => Number(a.timestamp ?? 0) - Number(b.timestamp ?? 0));
    return merged;
  }

  private _shouldEnsureCadenceRemindersVisible(): boolean {
    const executionId = String(this.config.executionId || "").trim();
    if (!executionId) return false;
    const selected = executionsStore.selectedExecution;
    if (!selected?.id || selected.id !== executionId) return false;
    return readProjectRunStatus(selected) === ProjectRunStatus.Running;
  }

  private _mergePinnedCadenceReminders(messages: ChatMessage[]): ChatMessage[] {
    const pinned = this._pinnedCadenceReminders;
    if (!pinned.length) return messages;
    const keys = new Set(messages.map((msg) => this._messageContentKey(msg)));
    const extras = pinned.filter((msg) => !keys.has(this._messageContentKey(msg)));
    if (!extras.length) return messages;
    const merged = [...messages, ...extras];
    merged.sort((a, b) => Number(a.timestamp ?? 0) - Number(b.timestamp ?? 0));
    return merged;
  }

  private async _ensureCadenceRemindersVisible(
    sessionKey: string,
    loadedMessages: ChatMessage[],
  ): Promise<void> {
    if (!this._shouldEnsureCadenceRemindersVisible()) {
      this._pinnedCadenceReminders = [];
      this._cadenceRemindersFetchAttempted = false;
      return;
    }
    const visibleCadence = loadedMessages.filter((msg) =>
      isRunningCadenceReminderMessage(String(msg.content || "")),
    );
    if (visibleCadence.length >= 2) {
      this._pinnedCadenceReminders = [];
      return;
    }
    if (this._cadenceRemindersFetchAttempted && this._pinnedCadenceReminders.length) {
      return;
    }
    this._cadenceRemindersFetchAttempted = true;
    const pageSize = 100;
    let offset = 0;
    const found: ChatMessage[] = [];
    for (let page = 0; page < 20; page += 1) {
      const result = await hermesClient.request<{ messages: ChatMessage[]; total?: number }>(
        "chat.history",
        { sessionKey, limit: pageSize, offset },
      );
      const batch = result?.messages ?? [];
      if (!batch.length) break;
      for (const msg of batch) {
        if (isRunningCadenceReminderMessage(String(msg.content || ""))) {
          found.push(msg);
        }
      }
      if (found.length >= MAX_PINNED_CADENCE_REMINDERS) break;
      offset += batch.length;
      const total = Number(result?.total ?? 0);
      if (total > 0 && offset >= total) break;
      if (batch.length < pageSize) break;
    }
    if (!found.length) return;
    found.sort((a, b) => Number(a.timestamp ?? 0) - Number(b.timestamp ?? 0));
    const tailKeys = new Set(loadedMessages.map((m) => this._messageContentKey(m)));
    const missing = found.filter((m) => !tailKeys.has(this._messageContentKey(m)));
    this._pinnedCadenceReminders = missing.slice(-MAX_PINNED_CADENCE_REMINDERS);
  }

  private async _ensureRunningBootstrapVisible(
    sessionKey: string,
    loadedMessages: ChatMessage[],
  ): Promise<void> {
    if (!this._shouldEnsureRunningBootstrapVisible()) {
      this._pinnedRunningBootstrap = null;
      this._runningBootstrapFetchAttempted = false;
      return;
    }
    if (
      loadedMessages.some(
        (msg) =>
          isRunningBootstrapInjectMessage(String(msg.content || "")) ||
          isPhasePromotedRunningMessage(String(msg.content || "")),
      )
    ) {
      this._pinnedRunningBootstrap = null;
      return;
    }
    if (this._pinnedRunningBootstrap || this._runningBootstrapFetchAttempted) {
      return;
    }
    this._runningBootstrapFetchAttempted = true;
    const pageSize = 100;
    let offset = 0;
    for (let page = 0; page < 30; page += 1) {
      const result = await hermesClient.request<{ messages: ChatMessage[]; total?: number }>(
        "chat.history",
        { sessionKey, limit: pageSize, offset },
      );
      const batch = result?.messages ?? [];
      if (!batch.length) break;
      const found = batch.find((msg) => {
        const content = String(msg.content || "");
        return (
          isRunningBootstrapInjectMessage(content) ||
          isPhasePromotedRunningMessage(content)
        );
      });
      if (found) {
        this._pinnedRunningBootstrap = found;
        return;
      }
      offset += batch.length;
      const total = Number(result?.total ?? 0);
      if (total > 0 && offset >= total) break;
      if (batch.length < pageSize) break;
    }
  }

  private _messagesForDisplay(messages: ChatMessage[]): ChatMessage[] {
    const merged = this._mergePinnedCadenceReminders(
      this._mergePinnedPhase1LoginInject(
        this._mergePinnedRunningBootstrap(messages),
      ),
    );
    const filtered = this._filterMessages(merged);
    const hasIntro = filtered.some(
      (msg) =>
        msg.role === "assistant" &&
        (String(msg.content || "").includes("## Run Introduction") ||
          String(msg.content || "")
            .toLowerCase()
            .includes("i have received the assignment")),
    );
    const briefing = (this.config.runBriefingMarkdown ?? "").trim();
    if (hasIntro || !briefing) {
      return filtered;
    }
    return [
      {
        role: "assistant",
        content: briefing,
        timestamp: Math.floor(Date.now() / 1000),
      },
      ...filtered,
    ];
  }

  private _composerPlaceholder(): string {
    if (!this._isLiveSessionReady()) {
      return "Type a message — it will send when this run is ready (Enter to queue)";
    }
    const t = this.config.title.trim() || "Assistant";
    return `Message ${t} (Enter to send)`;
  }

  render(): ReturnType<typeof html> {
    const filteredMessages = this._messagesForDisplay(this._messages);
    const fb = this.config.sessionKeySource === "fallback";
    const canCompose =
      !!(this.config.sessionKey ?? "").trim() &&
      !this.config.readonly &&
      (this.config.composerEnabled !== false || Boolean(this.config.executionId));

    return html`
      <div class="chat-container">
        ${
          fb
            ? html`
                <div class="session-banner" role="status">
                  Run session is still starting — messages are queued until the gateway finishes bootstrap.
                </div>
              `
            : ""
        }
        ${
          this.config.readonly
            ? html`
                <div class="session-banner" data-tone="info" role="status">
                  Read-only history for this view.
                </div>
              `
            : ""
        }
        ${
          this._errorMessage
            ? html`
                <div class="session-banner" role="alert">
                  ${this._errorMessage}
                </div>
              `
            : ""
        }
        <div class="chat-header">
          <h3>${this.config.title}</h3>
          <div class="chat-actions">
            <button
              class="btn-icon"
              type="button"
              @click=${() => void this._loadHistory()}
              title="Refresh"
            >
              ↻
            </button>
          </div>
        </div>

        <div
          class="messages"
          @wheel=${() => this._markScrollInteraction()}
          @touchmove=${() => this._markScrollInteraction()}
        >
          ${
            this._loading && filteredMessages.length === 0
              ? html`<div class="loading-state">Loading messages…</div>`
              : filteredMessages.length === 0
                ? html`<div class="empty-state">Waiting for the run briefing from the gateway. It usually appears within a few seconds of opening this run.</div>`
                : filteredMessages.map((msg) => this._renderMessage(msg))
          }
          ${
            (this.config.loginGateHint || this.config.loginAwaitingCredentials) &&
            !filteredMessages.some((msg) =>
              this._isRunIntroductionMessage(String(msg.content || "")),
            ) &&
            !this._agentActive &&
            !this._sending
              ? html`
                  <div class="session-banner" data-tone="info" role="status">
                    ${
                      this.config.loginGateHint
                        ? html`<p style="margin:0;">${this.config.loginGateHint}</p>`
                        : ""
                    }
                  </div>
                `
              : ""
          }
          ${
            this._sending
              ? html`
                  <div class="typing-indicator typing-indicator--outbound" role="status" aria-live="polite">
                    <div class="typing-dots" aria-hidden="true">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                `
              : ""
          }
        </div>

        ${
          this._deliveryQueue.length
            ? html`
                <div
                  class="session-banner"
                  data-tone="info"
                  role="status"
                  style="border-top:1px solid color-mix(in srgb,var(--border) 74%,transparent);margin:0;"
                >
                  ${this._deliveryQueue.length} message(s) queued — sending when the run session is
                  ready.
                </div>
              `
            : ""
        }
        ${
          canCompose
            ? html`
                <div class="input-area">
                  <div class="input-wrapper">
                    <textarea
                      class="input-field"
                      placeholder=${this._composerPlaceholder()}
                      .value=${this._inputValue}
                      ?disabled=${this._sending}
                      rows="1"
                      @input=${(e: Event) => {
                        this._inputValue = (e.target as HTMLTextAreaElement).value;
                        this._autoResizeComposer(e);
                      }}
                      @keydown=${this._handleKeyDown}
                    ></textarea>
                    <button
                      class="btn-send"
                      type="button"
                      data-sending=${this._sending ? "true" : "false"}
                      title=${this._sending ? "Sending your message…" : "Send message"}
                      ?disabled=${this._sending || !this._inputValue.trim()}
                      @click=${() => void this._sendMessage()}
                    >
                      ${this._sending ? html`<span class="btn-send__spinner" aria-hidden="true"></span>` : "➤"}
                    </button>
                  </div>
                </div>
              `
            : ""
        }
      </div>
    `;
  }

  private _renderMessage(msg: ChatMessage): ReturnType<typeof html> {
    const displayRole = this._messageDisplayRole(msg);
    const avatar =
      displayRole === "assistant"
        ? "AI"
        : displayRole === "ead-assistant"
          ? "EA"
          : displayRole === "system"
            ? "S"
            : displayRole === "tool"
              ? "T"
              : "U";
    const roleLabel =
      displayRole === "assistant"
        ? "Agent"
        : displayRole === "ead-assistant"
          ? "EAD Assistant"
          : displayRole === "system"
            ? "System"
            : displayRole === "tool"
              ? "Tool"
              : "You";
    const label = displayRole === "user" ? "You" : msg.label?.trim() || roleLabel;

    return html`
      <div class="message message--${displayRole}">
        ${
          this.config.showAvatars
            ? html`<div class="message-avatar ${displayRole}">${avatar}</div>`
            : ""
        }
        <div class="message-body">
          <div class="message-meta">
            <span>${label}</span>
            ${
              this.config.showTimestamps && msg.timestamp
                ? html`<span>${formatMs(msg.timestamp)}</span>`
                : ""
            }
            ${
              msg.relatedNodeId
                ? html`<span class="message-context">PFM node</span>`
                : ""
            }
          </div>
          ${displayRole === "tool"
            ? (() => {
                const preview = this._toolPreview(msg.content || "");
                return html`
                  <div class="message-content">
                    <div class="tool-compact">
                      <span class="tool-compact__line">${preview.line}</span>
                      <span class="tool-compact__icon" title=${preview.full}>i</span>
                    </div>
                  </div>
                `;
              })()
            : html`
                <div
                  class="message-content"
                  .innerHTML=${toSanitizedMarkdownHtml(msg.content || "")}
                ></div>
              `}
          ${
            msg.artifacts && msg.artifacts.length > 0
              ? html`
                  <div class="message-artifacts">
                    ${msg.artifacts.map(
                      (artifact) => html`
                        <div
                          class="artifact"
                          @click=${() => this._handleArtifactClick(artifact)}
                        >
                          ${this._getArtifactIcon(artifact.type)}
                          ${artifact.type}
                        </div>
                      `,
                    )}
                  </div>
                `
              : ""
          }
        </div>
      </div>
    `;
  }

  private _getArtifactIcon(type: string): string {
    const iconsMap: Record<string, string> = {
      screenshot: "📷",
      pfm_node: "◎",
      test_result: "✓",
      report: "▣",
    };
    return iconsMap[type] || "⌗";
  }

  private _toolPreview(content: string): { line: string; full: string } {
    const full = String(content || "").trim();
    const singleLine = full.replace(/\s+/g, " ").trim();
    if (!singleLine) {
      return { line: "Tool output available.", full: "No tool output." };
    }
    const line = singleLine.length > 140 ? `${singleLine.slice(0, 140)}…` : singleLine;
    return { line, full };
  }

  private _handleArtifactClick(artifact: {
    type: string;
    content: string;
  }): void {
    this.dispatchEvent(
      new CustomEvent("artifact-click", {
        detail: artifact,
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-chat-panel": EadChatPanel;
  }
}
